import { Box, makeStyles, Theme, Tooltip } from '@freighthub/silhouette-core'
import cn from 'classnames'
import { FC } from 'react'

import { PdadStepIcon } from '../PdadIcon'
import { TpProgressSegmentProps } from './TpProgressSegment'

export const testIds = {
  icon: 'shipment_list-progress_segment-milestone_icon',
}

type IconStyles = { size: number }
const useIconStyles = makeStyles<Theme, IconStyles>((theme) => ({
  progressIcon: {
    width: 32,
    height: 32,
    margin: theme.spacing(1, 0),
    borderRadius: '50%',
    color: theme.palette.text.disabled,
    border: `2px solid ${theme.palette.text.disabled}`,
    flex: '0 0 auto',
  },
  progressIconDone: {
    color: theme.palette.primary.main,
    border: `2px solid ${theme.palette.primary.main}`,
  },
  disabled: {
    opacity: 0.5,
  },
  stepIcon: ({ size }) => ({
    fontSize: `${size}px`,
  }),
}))

type MilestoneIconProps = Pick<
  TpProgressSegmentProps,
  'size' | 'tooltip' | 'isStepComplete' | 'isIconDisabled' | 'icon'
>

export const MilestoneIcon: FC<MilestoneIconProps> = ({ size, tooltip, isStepComplete, isIconDisabled, icon }) => {
  const classes = useIconStyles({ size })
  return (
    <Tooltip title={tooltip} placement="top">
      <Box
        justifyContent="center"
        alignItems="center"
        display="flex"
        className={cn(classes.progressIcon, {
          [classes.progressIconDone]: isStepComplete,
          [classes.disabled]: isIconDisabled,
        })}
        data-testid={testIds.icon}
      >
        <PdadStepIcon iconType={icon} className={classes.stepIcon} />
      </Box>
    </Tooltip>
  )
}

const useProgressStyles = makeStyles<Theme, Pick<ProgressProps, 'percentage'>>((theme) => ({
  progress: ({ percentage }) => ({
    width: `${percentage}%`,
  }),
  progressStep: {
    height: 4,
    backgroundColor: theme.palette.tag.info,
  },
  progressStepDone: {
    height: '100%',
    width: 0,
    backgroundColor: theme.palette.primary.main,
  },
  progressStepDisabled: {
    height: '100%',
    width: 0,
    backgroundImage: `linear-gradient(to right, ${theme.palette.tag.info} 0%, ${theme.palette.tag.info} 50%, white 50%)`,
    backgroundPosition: '0 top',
    backgroundSize: '10px 4px',
    backgroundRepeat: 'repeat-x',
  },
}))

type ProgressProps = {
  percentage: number
  isStepDisabled: boolean
}

export const Progress: FC<ProgressProps> = ({ percentage, isStepDisabled }) => {
  const classes = useProgressStyles({ percentage })
  return (
    <Box display="flex" justifyContent="space-between" alignItems="center" flex="1" className={classes.progressStep}>
      <div className={cn(classes.progress, isStepDisabled ? classes.progressStepDisabled : classes.progressStepDone)} />
    </Box>
  )
}
